import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type {
  CalibrationChannel,
  CalibrationProbe,
  CalibrationState,
  DeviceCalibration,
  RecordCalibrationBody,
  RevokeCalibrationBody,
} from './device-calibration.types';

const BASE = '/v1/devices/calibrations';

/** Every probe's CURRENT calibration — one round trip for a whole screen, rather
 *  than one per row. */
export function useDeviceCalibrations(enabled = true) {
  return useQuery({
    queryKey: ['device-calibrations'],
    queryFn: () => api.get<DeviceCalibration[]>(BASE),
    staleTime: 30_000,
    enabled,
  });
}

/** Which fields each sensor channel takes, with its unit and operating range.
 *
 *  Served from the device catalogue rather than hardcoded, so a unit or range the
 *  catalogue changes cannot silently disagree with the label on the field. */
export function useCalibrationChannels(enabled = true) {
  return useQuery({
    queryKey: ['calibration-channels'],
    queryFn: () => api.get<CalibrationChannel[]>(`${BASE}/channels`),
    staleTime: 300_000,
    enabled,
  });
}

/** What each bridge reports it is actually applying.
 *
 *  Polled, because it is the answer to "did my calibration reach the hardware" —
 *  the publisher reconciles every 20 s, so a change lands within a tick or two and
 *  an operator watching the panel should see it without a reload. */
export function useCalibrationStates(enabled = true) {
  return useQuery({
    queryKey: ['calibration-states'],
    queryFn: () => api.get<CalibrationState[]>(`${BASE}/state`),
    refetchInterval: 15_000,
    enabled,
  });
}

/** One probe's history, newest first — the audit trail behind every corrected
 *  reading this channel has shown. */
export function useCalibrationHistory(macAddress: string | null, channelCode: string | null) {
  return useQuery({
    queryKey: ['calibration-history', macAddress, channelCode],
    queryFn: () => api.get<DeviceCalibration[]>(`${BASE}/${macAddress}/${channelCode}`),
    enabled: Boolean(macAddress && channelCode),
  });
}

/** Record a calibration. **Appends — never edits.**
 *
 *  Invalidates the readings too: the bridge starts correcting within a publish
 *  tick, so the numbers on screen are about to change and a stale snapshot would
 *  contradict the panel that just accepted the value. */
export function useRecordDeviceCalibration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordCalibrationBody) => api.post<DeviceCalibration>(BASE, body),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['device-calibrations'] });
      qc.invalidateQueries({ queryKey: ['calibration-history', v.mac_address, v.channel_code] });
      qc.invalidateQueries({ queryKey: ['calibration-states'] });
      qc.invalidateQueries({ queryKey: ['calibration-probes'] });
    },
  });
}

/** Every sensor channel on the site, joined to its catalogue entry, its current
 *  correction, its place and what the bridge reports — the calibration screen's
 *  one read (backend R-332).
 *
 *  Polled on the same cadence as the bridge states, and for the same reason: half
 *  of what it returns is "did my calibration reach the hardware", which changes on
 *  the publisher's 20 s tick without anything happening in the browser.
 *
 *  ⚠ It reads the device topology and inventory as well as the calibrations, so the
 *  gateway checks `farming:read` AND `mapper:read`. Gate the screen on both or it
 *  loads and then 403s — the mistake the devices feature already made once. */
export function useCalibrationProbes(enabled = true) {
  return useQuery({
    queryKey: ['calibration-probes'],
    queryFn: () => api.get<CalibrationProbe[]>(`${BASE}/probes`),
    refetchInterval: 15_000,
    enabled,
  });
}

/** Stop correcting a probe — it returns to reporting exactly what it measures.
 *
 *  An APPEND, not a delete: the withdrawn correction stays in the trail, because
 *  past readings keep the correction that applied when they were taken. Before this
 *  existed a wrong correction could only be superseded by another non-identity one.
 *
 *  Invalidates the readings too — the bridge stops correcting within a publish tick,
 *  so the numbers on screen are about to change. */
export function useRevokeDeviceCalibration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RevokeCalibrationBody) => api.post<DeviceCalibration>(`${BASE}/revoke`, body),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['device-calibrations'] });
      qc.invalidateQueries({ queryKey: ['calibration-history', v.mac_address, v.channel_code] });
      qc.invalidateQueries({ queryKey: ['calibration-states'] });
      qc.invalidateQueries({ queryKey: ['calibration-probes'] });
    },
  });
}
